import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";

const DEBUG_STORAGE_KEY = "priceme_debug_storage";
const TAP_WINDOW_MS = 2000;
const TAP_COUNT_NEEDED = 5;
const CORNER_SIZE_PX = 48;

/** Returns true if (x, y) is in the bottom-right corner of the viewport. */
function isInBottomRightCorner(clientX: number, clientY: number): boolean {
    if (typeof window === "undefined") return false;
    const xOk = clientX >= window.innerWidth - CORNER_SIZE_PX;
    const yOk = clientY >= window.innerHeight - CORNER_SIZE_PX;
    return xOk && yOk;
}

/**
 * In-app storage inspector for mobile/webview testing.
 * Enable via:
 * 1) URL: ?debug=storage (e.g. https://yoursite.com/puzzle?debug=storage)
 * 2) In webview (no URL bar): tap the bottom-right corner 5 times quickly — then the Storage button appears
 * Use it to verify localStorage, sessionStorage, and cache in Reddit/in-app webviews.
 */
export default function StorageDebugPanel() {
    const [open, setOpen] = useState(false);
    const [local, setLocal] = useState<Record<string, string>>({});
    const [session, setSession] = useState<Record<string, string>>({});
    const [cacheInfo, setCacheInfo] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [forceShow, setForceShow] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const hasUrlParam =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("debug") === "storage";
    let hasLocalStorageFlag = false;
    if (typeof window !== "undefined") {
        try {
            hasLocalStorageFlag = window.localStorage?.getItem(DEBUG_STORAGE_KEY) === "true";
        } catch {
            // localStorage can throw (e.g. SecurityError in webviews/private mode)
            hasLocalStorageFlag = false;
        }
    }
    const isDebug = hasUrlParam || hasLocalStorageFlag || forceShow;

    const refresh = () => {
        setError(null);
        try {
            const l: Record<string, string> = {};
            for (let i = 0; i < window.localStorage.length; i++) {
                const k = window.localStorage.key(i);
                if (k) l[k] = window.localStorage.getItem(k) ?? "";
            }
            setLocal(l);

            const s: Record<string, string> = {};
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const k = window.sessionStorage.key(i);
                if (k) s[k] = window.sessionStorage.getItem(k) ?? "";
            }
            setSession(s);

            if ("caches" in window) {
                window.caches
                    .keys()
                    .then((names) => {
                        if (mountedRef.current) {
                            setCacheInfo(`Cache names (${names.length}): ${names.join(", ") || "none"}`);
                        }
                    })
                    .catch((e) => {
                        if (mountedRef.current) {
                            setCacheInfo("Cache API error");
                            setError(e instanceof Error ? e.message : String(e));
                        }
                    });
            } else {
                setCacheInfo("Cache API not available");
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    useEffect(() => {
        if (!isDebug) return;
        refresh();
    }, [isDebug, open]);

    const handleSecretTap = useCallback(() => {
        const now = Date.now();
        if (typeof window === "undefined") return;
        const state = (window as any).__storageDebugTapState ?? { count: 0, firstAt: 0 };
        if (now - state.firstAt > TAP_WINDOW_MS) {
            state.count = 0;
            state.firstAt = now;
        }
        state.count++;
        (window as any).__storageDebugTapState = state;
        if (state.count >= TAP_COUNT_NEEDED) {
            try {
                window.localStorage.setItem(DEBUG_STORAGE_KEY, "true");
                setForceShow(true);
                toast.success("Storage debug enabled");
            } finally {
                state.count = 0;
            }
        }
    }, []);

    // Global listener: only count taps in the bottom-right corner; no overlay so UI stays clickable
    useEffect(() => {
        if (isDebug) return;

        const handlePointer = (clientX: number, clientY: number) => {
            if (isInBottomRightCorner(clientX, clientY)) handleSecretTap();
        };

        const onClick = (e: MouseEvent) => handlePointer(e.clientX, e.clientY);
        const onTouchEnd = (e: TouchEvent) => {
            const t = e.changedTouches?.[0];
            if (t) handlePointer(t.clientX, t.clientY);
        };

        document.addEventListener("click", onClick, { passive: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true });
        return () => {
            document.removeEventListener("click", onClick);
            document.removeEventListener("touchend", onTouchEnd);
        };
    }, [isDebug, handleSecretTap]);

    return (
        <>
            {isDebug && (
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    position: "fixed",
                    bottom: 16,
                    right: 16,
                    zIndex: 99999,
                    padding: "8px 12px",
                    fontSize: 12,
                    background: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
                aria-label="Toggle storage debug"
            >
                Storage
            </button>
            )}
            {isDebug && open && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 99998,
                        background: "rgba(0,0,0,0.85)",
                        color: "#eee",
                        padding: 16,
                        overflow: "auto",
                        fontSize: 12,
                        fontFamily: "monospace",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                        <strong>Storage (Application-like)</strong>
                        <div>
                            <button
                                type="button"
                                onClick={refresh}
                                style={{
                                    marginRight: 8,
                                    padding: "4px 8px",
                                    background: "#555",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                }}
                            >
                                Refresh
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                style={{
                                    marginRight: 8,
                                    padding: "4px 8px",
                                    background: "#555",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                }}
                            >
                                Close
                            </button>
                            {hasLocalStorageFlag && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        window.localStorage?.removeItem(DEBUG_STORAGE_KEY);
                                        setForceShow(false);
                                        setOpen(false);
                                        toast.success("Storage debug disabled");
                                    }}
                                    style={{
                                        padding: "4px 8px",
                                        background: "#555",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 4,
                                    }}
                                >
                                    Disable debug
                                </button>
                            )}
                        </div>
                    </div>
                    <div style={{ marginBottom: 8, opacity: 0.8, fontSize: 11 }}>
                        Enabled via URL ?debug=storage or 5 taps in bottom-right corner (webview).
                    </div>
                    {error && <div style={{ color: "#f88", marginBottom: 8 }}>Error: {error}</div>}
                    <section style={{ marginBottom: 16 }}>
                        <strong>localStorage</strong> ({Object.keys(local).length} keys)
                        <pre
                            style={{
                                background: "#222",
                                padding: 8,
                                borderRadius: 4,
                                overflow: "auto",
                                maxHeight: 200,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                            }}
                        >
                            {JSON.stringify(local, null, 2)}
                        </pre>
                    </section>
                    <section style={{ marginBottom: 16 }}>
                        <strong>sessionStorage</strong> ({Object.keys(session).length} keys)
                        <pre
                            style={{
                                background: "#222",
                                padding: 8,
                                borderRadius: 4,
                                overflow: "auto",
                                maxHeight: 200,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                            }}
                        >
                            {JSON.stringify(session, null, 2)}
                        </pre>
                    </section>
                    <section>
                        <strong>Cache API</strong>
                        <div style={{ marginTop: 4 }}>{cacheInfo || "Loading…"}</div>
                    </section>
                </div>
            )}
        </>
    );
}
